import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  useDeleteActivityMutation,
  useEntityActivitiesFetch,
  useFilteredActivities,
  useUpdateActivityMutation
} from "@/entities/activities/hooks";
import { useTranslation } from "react-i18next";
import { Add, UpcomingActivity } from "@dreamhub/designsystem/icons";
import ImportTranscriptModal from "@/components/controls/ImportTranscriptModal";
import ExportTranscriptUnavailableModal from "@/components/controls/ExportTranscriptUnavailableModal";
import InviteNotetakerModal, {
  normalizeMeetingUrl
} from "@/components/controls/InviteNotetakerModal";
import { useGetAttachment } from "@/entities/attachments/hooks";
import { downloadBlob } from "@/entities/attachments/helpers";
import { useFeatureFlag } from "@/services/features/hook";
import { useIsNoteTakerAvailable } from "@/services/auth/useIsNoteTakerAvailable";
import { FeatureFlags } from "@/services/features/types";
import Text from "@/components/ui/text";
import {
  Activity as ActivityType,
  ActivityData,
  ActivityGroup,
  ActivityTypeCount,
  EntityActivityResponse,
  NewActivity
} from "@/entities/activities/types";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";
import ActivityMonth, {
  UPCOMING_MONTH
} from "@/components/layouts/Activities/components/ActivityMonth";
import ActivityClosedRecordEmptyState, {
  ClosedRecordActivityState
} from "@/components/layouts/Activities/components/ActivityClosedRecordEmptyState";
import { TFunction } from "i18next";
import {
  activityGroupToTypesMap,
  buildRemoveActivityTagUpdate,
  buildFetchRequest,
  generateTranscriptFilename,
  groupActivitiesByMonth,
  newActivityTypes,
  updateActivityTagsInFetchResponse
} from "@/entities/activities/helpers";
import { EntityData } from "@/services/api/types";
import {
  activityTypeToIconMap,
  activityTypeToNewActivityType
} from "@/components/layouts/Activities/components/Activity";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import toast from "react-hot-toast";
import SearchNavigation from "@/components/controls/SerachNavigation/SearchNavigation";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import {
  ActivityModalState,
  useActivityModals
} from "@/components/layouts/Activities/hooks/useActivityModals";
import InlineLoader from "@/components/ui/inline-loader";
import { createModalStateSetter } from "@/entities/modals/helpers";
import { useSplitActivityByTimes } from "@/entities/activities/timehook";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import IconContainer from "@/components/ui/icon-container";
import Tabs from "@/components/controls/Tabs";
import TabBadge from "@/components/ui/tab-badge";
import { find, forEach, isEmpty, isNull, keys, map, sumBy } from "lodash";
import { cn } from "@/utils/utils";

export interface DeleteActivityModalState {
  isOpen: boolean;
  activityId: string | null;
}

export const newActivityToActivityType: {
  [key in NewActivity]: ActivityType;
} = {
  [NewActivity.CALL]: ActivityType.CALL,
  [NewActivity.MEETING]: ActivityType.IN_PERSON_MEETING,
  [NewActivity.EMAIL]: ActivityType.EMAIL,
  [NewActivity.TEXT]: ActivityType.TEXT,
  [NewActivity.NOTE]: ActivityType.NOTE,
  [NewActivity.NDA]: ActivityType.NDA_SENT,
  [NewActivity.QUOTE]: ActivityType.QUOTE_SENT,
  [NewActivity.SLACK_MESSAGE]: ActivityType.SLACK_MESSAGE
};

export const getEntityTypeFromPageName = (
  pageName: string
): EntityType | undefined => {
  const entry = find(
    Object.entries(entityTypeToEntityName),
    ([_, value]) => value === pageName
  );
  return entry ? (Number(entry[0]) as EntityType) : undefined;
};

const getCountByActivityGroupFromCountPerType = (
  group: string,
  countPerType: ActivityTypeCount[]
): number => {
  if (group === ActivityGroup.ALL.toString()) {
    return sumBy(countPerType, (entry) => entry.count);
  }

  if (group === ActivityGroup.OTHERS.toString()) {
    const mainTypes = activityGroupToTypesMap[
      ActivityGroup.OTHERS.toString()
    ] as number[];

    return sumBy(countPerType, (entry) =>
      !mainTypes.includes(entry.activityType) ? entry.count : 0
    );
  }

  const groupTypes =
    activityGroupToTypesMap[group as keyof typeof activityGroupToTypesMap] ||
    [];
  if (groupTypes.length === 0) return 0;

  return sumBy(countPerType, (entry) =>
    (groupTypes as number[]).includes(entry.activityType) ? entry.count : 0
  );
};

const getActivityGroupOptionsFromCountPerType = (
  t: TFunction<"translation", undefined>,
  countPerType: ActivityTypeCount[]
): { [key: string]: string | JSX.Element } => {
  const options: { [key: string]: string | JSX.Element } = {};
  forEach(
    keys(ActivityGroup).filter((key) => !isNaN(Number(key))),
    (group) => {
      const groupCount = getCountByActivityGroupFromCountPerType(
        group,
        countPerType
      );
      let label = t(
        getEnumTKey("activities", "tabs", ActivityGroup, Number(group))
      );
      if (group === ActivityGroup.ALL.toString()) {
        label = `${label} activities`;
      }
      options[group] = <TabBadge label={label} count={groupCount} />;
    }
  );
  return options;
};

const createEntityCreationActivity = (
  entityData: EntityData,
  entityType: EntityType,
  t: TFunction
): ActivityData => ({
  id: "created-at-activity",
  createdAt: entityData.createdAt,
  dealId: entityData.id,
  leadId: null,
  notes: {
    subject: `${t(getEnumTKey("entities", "names", EntityType, entityType))} ${t("activities.created")}`,
    date: entityData.createdAt
  }
});

interface Props {
  pageName: string;
  entityData: EntityData;
  entityType: EntityType;
  isTasksDrawerOpen: boolean;
  /**
   * When set, the record has been converted/inactivated: the composer is
   * replaced by a closed-record empty state and no new activity can be added.
   */
  closedRecordState?: ClosedRecordActivityState;
}

const Activities: React.FC<Props> = ({
  pageName,
  entityData,
  entityType,
  isTasksDrawerOpen,
  closedRecordState
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const entityId = entityData?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where this record lives — used by the invite-notetaker success toast,
  // which can outlive the page
  const recordPathRef = useRef(location.pathname);
  recordPathRef.current = location.pathname;
  const [tabValue, setTabValue] = useState(ActivityGroup.ALL.toString());
  const [searchQuery, setSearchQuery] = useState("");
  const [activityModalState, setActivityModalState] =
    useState<ActivityModalState>({
      newActivityType: null,
      isOpen: false,
      isEditMode: false,
      editActivityData: null
    });
  const [deleteActivityModalState, setDeleteActivityModalState] =
    useState<DeleteActivityModalState>({
      isOpen: false,
      activityId: null
    });
  const [importTranscriptModalState, setImportTranscriptModalState] = useState<{
    isOpen: boolean;
    activity: ActivityData | null;
  }>({
    isOpen: false,
    activity: null
  });
  const [exportTranscript, setExportTranscript] = useState<{
    attachmentId: string;
    meetingTitle: string | undefined;
  } | null>(null);
  const [inviteNotetakerModalState, setInviteNotetakerModalState] = useState<{
    isOpen: boolean;
    activity: ActivityData | null;
  }>({
    isOpen: false,
    activity: null
  });
  const [isExportUnavailableOpen, setIsExportUnavailableOpen] = useState(false);
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(
    searchParams.get("expandedActivityId")
  );
  const [fetchResponse, setFetchResponse] =
    useState<EntityActivityResponse | null>(null);
  const [tabCounts, setTabCounts] = useState<ActivityTypeCount[]>([]);
  const fetchRequestId = useRef(0);

  const { isDropdownClosing, onDropdownOpenChange } = useDropdownClosing();
  const { enabled: inviteNotetakerEnabled } = useFeatureFlag(
    FeatureFlags.CRM_7174_INVITE_NOTETAKER
  );
  const isNoteTakerAvailable = useIsNoteTakerAvailable();

  const deleteActivity = useDeleteActivityMutation(pageName);
  const updateActivity = useUpdateActivityMutation(pageName);
  const entityActivitiesFetch = useEntityActivitiesFetch(pageName);

  const { data: exportAttachmentData } = useGetAttachment(
    exportTranscript?.attachmentId ?? ""
  );

  const handleImportTranscript = useCallback((activity: ActivityData) => {
    setImportTranscriptModalState({ isOpen: true, activity });
  }, []);

  const handleExportTranscript = useCallback((activity: ActivityData) => {
    if (activity.notes?.transcriptAttachmentId) {
      setExportTranscript({
        attachmentId: activity.notes.transcriptAttachmentId,
        meetingTitle: activity.notes.subject
      });
    } else {
      setIsExportUnavailableOpen(true);
    }
  }, []);

  const handleInviteNotetaker = useCallback((activity: ActivityData) => {
    setInviteNotetakerModalState({ isOpen: true, activity });
  }, []);

  const onImportTranscript = handleImportTranscript;
  const onExportTranscript = handleExportTranscript;
  const onInviteNotetaker =
    inviteNotetakerEnabled && isNoteTakerAvailable
      ? handleInviteNotetaker
      : undefined;

  useEffect(() => {
    if (exportAttachmentData && exportTranscript?.attachmentId) {
      const sourceFilename = exportAttachmentData.filename ?? "";
      const lastDotIndex = sourceFilename.lastIndexOf(".");
      const extension =
        lastDotIndex > -1 && lastDotIndex < sourceFilename.length - 1
          ? sourceFilename.slice(lastDotIndex + 1)
          : "txt";
      downloadBlob({
        ...exportAttachmentData,
        filename: generateTranscriptFilename(
          exportTranscript.meetingTitle,
          extension
        )
      });
      setExportTranscript(null);
    }
  }, [exportAttachmentData, exportTranscript]);

  const activities = fetchResponse?.activities || [];

  // A deep-linked activity (e.g. from a task's "View Activity" link) can fall
  // outside the default fetch window/size the tab loads on mount — fetch it
  // directly by id so it still renders and expands instead of silently no-op'ing.
  const missingExpandedActivityId =
    fetchResponse && expandedActivityId &&
    !activities.some((activity) => activity.id === expandedActivityId)
      ? expandedActivityId
      : "";
  const {
    entities: [expandedActivityFallback]
  } = useFilteredActivities(
    pageName,
    entityId || "",
    missingExpandedActivityId ? { id: { eq: missingExpandedActivityId } } : {},
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    Boolean(missingExpandedActivityId)
  );

  const activitiesWithExpandedFallback = expandedActivityFallback
    ? [...activities, expandedActivityFallback]
    : activities;

  const { upcomingActivities, pastActivities } = useSplitActivityByTimes(
    activitiesWithExpandedFallback
  );

  const tabCountsFetch = useEntityActivitiesFetch(pageName);

  const refetchTabCounts = useCallback(() => {
    if (!entityId) return;
    tabCountsFetch.mutate(
      {
        pathParams: { entityId },
        data: buildFetchRequest(ActivityGroup.ALL.toString())
      },
      {
        onSuccess: (response) => setTabCounts(response.countPerType),
        onError: () => setTabCounts([])
      }
    );
  }, [entityId]);

  const refetchActivities = useCallback(() => {
    if (!entityId) return;
    const requestId = ++fetchRequestId.current;
    entityActivitiesFetch.mutate(
      { pathParams: { entityId }, data: buildFetchRequest(tabValue) },
      {
        onSuccess: (response) => {
          if (requestId !== fetchRequestId.current) return;
          setFetchResponse(response);
          if (tabValue === ActivityGroup.ALL.toString()) {
            setTabCounts(response.countPerType);
          }
        },
        onError: () => {
          if (requestId !== fetchRequestId.current) return;
          setFetchResponse(null);
        }
      }
    );
  }, [entityId, tabValue]);

  useEffect(() => {
    refetchActivities();
  }, [refetchActivities]);

  useEffect(() => {
    refetchTabCounts();
  }, [refetchTabCounts]);

  useEffect(() => {
    const expandedId = searchParams.get("expandedActivityId");
    if (expandedId && expandedId !== expandedActivityId) {
      setExpandedActivityId(expandedId);
    }
  }, [searchParams, expandedActivityId]);

  const clearExpandedActivityId = () => {
    setExpandedActivityId(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("expandedActivityId");
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleViewInvitedMeeting = useCallback(
    (activityId: string) => {
      // The toast outlives this record's page — navigate back to the record
      // before expanding, in case the user has moved elsewhere meanwhile.
      const params = new URLSearchParams();
      params.set("expandedActivityId", activityId);
      navigate(`${recordPathRef.current}?${params.toString()}`);
      setExpandedActivityId(activityId);
    },
    [navigate]
  );

  const setIsActivityModalOpen = createModalStateSetter(setActivityModalState);

  const setIsDeleteActivityModalOpen = createModalStateSetter(
    setDeleteActivityModalState
  );

  // The record carries fields derived from its own activities, so adding,
  // editing, deleting or retagging one leaves them stale. The list and the
  // counts each come from their own query, so refetching those alone keeps the
  // record showing pre-save values until the user reloads the page. A record is
  // fetched under the bare entity name on most pages and under an "<entity>/id"
  // endpoint on others, so both keys are invalidated.
  const refetchActivitiesAndCounts = useCallback(() => {
    refetchActivities();
    refetchTabCounts();
    queryClient.invalidateQueries({ queryKey: [pageName] });
    queryClient.invalidateQueries({ queryKey: [`${pageName}/id`] });
  }, [refetchActivities, refetchTabCounts, queryClient, pageName]);

  const handleImportTranscriptSuccess = useCallback(() => {
    refetchActivities();
  }, [refetchActivities]);

  const { modals } = useActivityModals({
    activityModalState,
    isDropdownClosing,
    entityData,
    setIsActivityModalOpen,
    resetActivitiesInfinite: refetchActivitiesAndCounts,
    entityType,
    pageName,
    onImportTranscript,
    onExportTranscript
  });

  const openEdit = (activityType: ActivityType, activityData: ActivityData) => {
    const newActivityType = activityTypeToNewActivityType[activityType];
    setActivityModalState({
      isOpen: true,
      newActivityType,
      isEditMode: true,
      editActivityData: activityData
    });
  };

  const openDelete = (activityId: string) => {
    setDeleteActivityModalState({
      isOpen: true,
      activityId
    });
  };

  const handleDeleteActivity = () => {
    if (isNull(deleteActivityModalState.activityId) || !entityId) return;
    deleteActivity.mutate(
      {
        pathParams: {
          entityId,
          activityId: deleteActivityModalState.activityId
        }
      },
      {
        onSuccess: () => {
          setDeleteActivityModalState({
            isOpen: false,
            activityId: null
          });
          refetchActivitiesAndCounts();
          toast.success(t("activities.activityModal.activityDeleted"), {
            duration: 5000,
            icon: null
          });
        }
      }
    );
  };

  const handleRemoveTag = (activity: ActivityData, tagId: string) => {
    if (!entityId || !activity.id) return;
    updateActivity.mutate(
      {
        pathParams: { entityId, activityId: activity.id },
        data: buildRemoveActivityTagUpdate(activity, tagId)
      },
      {
        onSuccess: (response) => {
          setFetchResponse((currentResponse) =>
            updateActivityTagsInFetchResponse(currentResponse, response)
          );
          refetchActivitiesAndCounts();
        },
        onError: () =>
          toast.error(t("activities.activityModal.tagRemoveError"))
      }
    );
  };

  const getActivityTypeOptions = () => {
    const options = {} as DetailedViewOptions;
    forEach(newActivityTypes, (newActivity) => {
      options[newActivity] = {
        action: () => {
          setActivityModalState({
            isOpen: true,
            newActivityType: newActivity,
            isEditMode: false,
            editActivityData: null
          });
        },
        customIcon: (
          <IconContainer size="5" color="secondary">
            {
              activityTypeToIconMap[
                newActivityToActivityType[newActivity as NewActivity]
              ]
            }
          </IconContainer>
        ),
        customText: t(
          getEnumTKey("activities", "newActivities", NewActivity, newActivity)
        )
      };
    });
    return options;
  };

  const renderUpcomingActivities = () =>
    isEmpty(upcomingActivities) ? (
      <div className="flex px-2 w-full flex-col gap-2 items-center">
        <UpcomingActivity />
        <Text variant="bodyTertiary">
          {t("deals.nextSteps.upcomingActivities.empty")}
        </Text>
      </div>
    ) : (
      <ActivityMonth
        month={UPCOMING_MONTH}
        customTitle={t("activities.upcoming.title")}
        activities={upcomingActivities}
        searchQuery={searchQuery}
        openEdit={openEdit}
        openDelete={openDelete}
        onDropdownOpenChange={onDropdownOpenChange}
        isTasksDrawerOpen={isTasksDrawerOpen}
        expandedActivityId={expandedActivityId}
        clearExpandedActivityId={clearExpandedActivityId}
        onImportTranscript={onImportTranscript}
        onExportTranscript={onExportTranscript}
        onInviteNotetaker={onInviteNotetaker}
        onRemoveTag={
          entityId && !updateActivity.isPending ? handleRemoveTag : undefined
        }
      />
    );

  const renderActivities = () => {
    const allActivities =
      entityData && tabValue === ActivityGroup.ALL.toString()
        ? [
            ...pastActivities,
            createEntityCreationActivity(entityData, entityType, t)
          ]
        : pastActivities;

    const { months: sortedMonths, byMonth: sortedGroupedActivities } =
      groupActivitiesByMonth(allActivities);

    return map(sortedMonths, (month) => (
      <ActivityMonth
        key={month}
        month={month}
        activities={sortedGroupedActivities[month]}
        searchQuery={searchQuery}
        openEdit={openEdit}
        openDelete={openDelete}
        onDropdownOpenChange={onDropdownOpenChange}
        isTasksDrawerOpen={isTasksDrawerOpen}
        expandedActivityId={expandedActivityId}
        clearExpandedActivityId={clearExpandedActivityId}
        onImportTranscript={onImportTranscript}
        onExportTranscript={onExportTranscript}
        onInviteNotetaker={onInviteNotetaker}
        onRemoveTag={
          entityId && !updateActivity.isPending ? handleRemoveTag : undefined
        }
      />
    ));
  };

  return (
    <>
      <div
        className={cn(
          "flex flex-col w-full max-w-[1800px] mx-auto gap-12 overflow-auto",
          isTasksDrawerOpen ? "px-8" : "px-32"
        )}
        data-test="activities-container"
      >
        <div
          className="w-full flex justify-between"
          data-test="activities-header"
        >
          <Tabs
            options={getActivityGroupOptionsFromCountPerType(t, tabCounts)}
            defaultValue={tabValue}
            valueSelected={(value) => setTabValue(value)}
            dataTest="activities-tabs"
            variant="pill"
            size="lg"
            fullWidth={false}
          />
          <div className="flex gap-2 h-9" data-test="activities-actions">
            <SearchNavigation
              className="h-full max-w-[22rem] min-w-[12rem]"
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              data-test="activities-search"
            />
            {!closedRecordState && (
              <OptionsDropdownMenu
                options={getActivityTypeOptions()}
                tPrefix="detailedView.create"
                customTrigger={
                  <Button size="lg" data-test="activities-add-button">
                    <Add />
                    {t("activities.add")}
                  </Button>
                }
              />
            )}
          </div>
        </div>
        <div
          className="flex flex-col w-full gap-12"
          data-test="activities-content"
        >
          {closedRecordState ? (
            <ActivityClosedRecordEmptyState {...closedRecordState} />
          ) : (
            renderUpcomingActivities()
          )}
          <div className="flex flex-col gap-8" data-test="activities-list">
            {renderActivities()}
          </div>
        </div>
        {entityActivitiesFetch.isPending && (
          <div
            className="w-full py-4 text-center"
            data-test="activities-loader"
          >
            <InlineLoader label={t("loading")} />
          </div>
        )}
        {modals}
        <Modal
          open={deleteActivityModalState.isOpen && !isDropdownClosing}
          setIsOpen={setIsDeleteActivityModalOpen}
          contentClassname="w-[35rem]"
          data-test="activities-delete-modal"
          title={
            <Text as="div" variant="lgSubtitleH1">
              {`${t("deleteEntityModal.title")} ${t("activities.activityModal.activity")}`}
            </Text>
          }
          content={
            <div className="flex flex-col gap-3 h-fit w-[35rem] p-4">
              <Text as="div" variant="body">
                {t("activities.activityModal.deleteActivity")}
              </Text>
            </div>
          }
          footer={
            <div
              className="flex justify-end gap-2"
              data-test="activities-delete-modal-footer"
            >
              <Button
                data-test="activities-delete-modal-cancel-button"
                variant="secondary"
                onClick={() =>
                  setDeleteActivityModalState({
                    isOpen: false,
                    activityId: null
                  })
                }
              >
                {t("cancel")}
              </Button>
              <Button
                data-test="activities-delete-modal-confirm-button"
                destructive
                onClick={handleDeleteActivity}
              >
                {t("deleteEntityModal.delete")}
              </Button>
            </div>
          }
        />
        {importTranscriptModalState.activity && (
          <ImportTranscriptModal
            isOpen={importTranscriptModalState.isOpen}
            onClose={() =>
              setImportTranscriptModalState({ isOpen: false, activity: null })
            }
            activity={importTranscriptModalState.activity}
            pageName={pageName}
            entityId={entityId}
            hasExistingTranscript={
              !!importTranscriptModalState.activity?.notes?.hasTranscript
            }
            onSuccess={handleImportTranscriptSuccess}
          />
        )}
        <ExportTranscriptUnavailableModal
          labels={{
            title: t("activities.transcript.exportUnavailableTitle"),
            body: t("activities.transcript.exportUnavailableBody"),
            confirm: t("activities.transcript.gotIt")
          }}
          isOpen={isExportUnavailableOpen}
          onClose={() => setIsExportUnavailableOpen(false)}
        />
        {inviteNotetakerModalState.activity && (
          <InviteNotetakerModal
            key={inviteNotetakerModalState.activity.id}
            isOpen={inviteNotetakerModalState.isOpen}
            onClose={() =>
              setInviteNotetakerModalState((prev) => ({
                ...prev,
                isOpen: false
              }))
            }
            onRetry={() =>
              setInviteNotetakerModalState((prev) => ({
                ...prev,
                isOpen: true
              }))
            }
            initialMeetingUrl={
              normalizeMeetingUrl(
                inviteNotetakerModalState.activity.notes?.location ?? ""
              ) ?? undefined
            }
            scheduledJoinAt={inviteNotetakerModalState.activity.notes?.date}
            meetingTitle={inviteNotetakerModalState.activity.notes?.subject}
            onViewMeeting={() =>
              handleViewInvitedMeeting(inviteNotetakerModalState.activity!.id)
            }
          />
        )}
      </div>
    </>
  );
};

export default Activities;
